import { createSlice } from '@reduxjs/toolkit';

import eventBus, { EventType } from '@utils/eventBus';

import {
  handleAdminHighRiskSupplementsList, handleAdminImportIngredientsCleanUpList,
  handleAdminImportMedications,
  handleAdminImportSupplementsCleanUp,
  handleAdminImportSynonymsList,
} from './actions';
import { IAdminImportMedicationsState } from './slice.interface';

const initialState: IAdminImportMedicationsState = {
  isLoading: false,
  errors: [],
  isLoadingSynonymsList: false,
  errorsSynonymsList: [],
  isLoadingHighRiskSupplementsList: false,
  errorsHighRiskSupplementsList: [],
  isLoadingSupplementsCleanUpList: false,
  errorsSupplementsCleanUpList: [],
  isLoadingIngredientsCleanUpList: false,
  errorsIngredientsCleanUpList: [],
};

export const adminImportMedicationsSlice = createSlice({
  name: 'admin-import-medications',
  initialState,
  reducers: {
    removeAdminImportMedications: (state: IAdminImportMedicationsState) => {
      state.isLoading = false;
      state.errors = [];
    },
    removeAdminImportSynonymsList: (state: IAdminImportMedicationsState) => {
      state.isLoadingSynonymsList = false;
      state.errorsSynonymsList = [];
    },
    removeAdminImportHighRiskSupplementsList: (state: IAdminImportMedicationsState) => {
      state.isLoadingHighRiskSupplementsList = false;
      state.errorsHighRiskSupplementsList = [];
    },
    removeAdminImportSupplementsCleanUpList: (state: IAdminImportMedicationsState) => {
      state.isLoadingSupplementsCleanUpList = false;
      state.errorsSupplementsCleanUpList = [];
    },
    removeAdminImportIngredientsCleanUpList: (state: IAdminImportMedicationsState) => {
      state.isLoadingIngredientsCleanUpList = false;
      state.errorsIngredientsCleanUpList = [];
    },
  },
  extraReducers: builder => {
    builder
      .addCase(handleAdminImportMedications.pending, (state: IAdminImportMedicationsState) => {
        state.isLoading = true;
        state.errors = [];
      })
      .addCase(handleAdminImportMedications.rejected, (state: IAdminImportMedicationsState) => {
        state.isLoading = false;
      })
      .addCase(
        handleAdminImportMedications.fulfilled,
        (state: IAdminImportMedicationsState, action) => {
          state.isLoading = false;

          if (action.payload?.data?.errors?.length) {
            state.errors = action.payload.data.errors;
          } else {
            eventBus.emit(EventType.ADMIN_IMPORT_MEDICATIONS_SUCCESS);
          }
        },
      )
      .addCase(handleAdminImportSynonymsList.pending, (state: IAdminImportMedicationsState) => {
        state.isLoadingSynonymsList = true;
        state.errorsSynonymsList = [];
      })
      .addCase(handleAdminImportSynonymsList.rejected, (state: IAdminImportMedicationsState) => {
        state.isLoadingSynonymsList = false;
      })
      .addCase(
        handleAdminImportSynonymsList.fulfilled,
        (state: IAdminImportMedicationsState, action) => {
          state.isLoadingSynonymsList = false;

          if (action.payload?.data?.errors?.length) {
            state.errorsSynonymsList = action.payload.data.errors;
          } else {
            eventBus.emit(EventType.ADMIN_IMPORT_SYNONYMS_LIST_SUCCESS);
          }
        },
      )
      .addCase(
        handleAdminHighRiskSupplementsList.pending,
        (state: IAdminImportMedicationsState) => {
          state.isLoadingHighRiskSupplementsList = true;
          state.errorsHighRiskSupplementsList = [];
        },
      )
      .addCase(
        handleAdminHighRiskSupplementsList.rejected,
        (state: IAdminImportMedicationsState) => {
          state.isLoadingHighRiskSupplementsList = false;
        },
      )
      .addCase(
        handleAdminHighRiskSupplementsList.fulfilled,
        (state: IAdminImportMedicationsState, action) => {
          state.isLoadingHighRiskSupplementsList = false;
          if (Array.isArray(action.payload?.data?.errors) && action.payload.data.errors.length) {
            state.errorsHighRiskSupplementsList = action.payload.data.errors;
            return;
          }
          eventBus.emit(EventType.ADMIN_IMPORT_HIGH_RISK_SUPPLEMENTS_LIST_SUCCESS);
        },
      )
      .addCase(
        handleAdminImportSupplementsCleanUp.pending,
        (state: IAdminImportMedicationsState) => {
          state.isLoadingSupplementsCleanUpList = true;
          state.errorsSupplementsCleanUpList = [];
        },
      )
      .addCase(
        handleAdminImportSupplementsCleanUp.rejected,
        (state: IAdminImportMedicationsState) => {
          state.isLoadingSupplementsCleanUpList = false;
        },
      )
      .addCase(
        handleAdminImportSupplementsCleanUp.fulfilled,
        (state: IAdminImportMedicationsState, action) => {
          state.isLoadingSupplementsCleanUpList = false;
          if (Array.isArray(action.payload?.data?.errors) && action.payload.data.errors.length) {
            state.errorsSupplementsCleanUpList = action.payload.data.errors;
            return;
          }
          eventBus.emit(EventType.ADMIN_IMPORT_SUPPLEMENTS_CLEANUP_LIST_SUCCESS);
        },
      )
      .addCase(
        handleAdminImportIngredientsCleanUpList.pending,
        (state: IAdminImportMedicationsState) => {
          state.isLoadingIngredientsCleanUpList = true;
          state.errorsIngredientsCleanUpList = [];
        },
      )
      .addCase(
        handleAdminImportIngredientsCleanUpList.rejected,
        (state: IAdminImportMedicationsState) => {
          state.isLoadingIngredientsCleanUpList = false;
        },
      )
      .addCase(
        handleAdminImportIngredientsCleanUpList.fulfilled,
        (state: IAdminImportMedicationsState, action) => {
          state.isLoadingIngredientsCleanUpList = false;
          if (Array.isArray(action.payload?.data?.errors) && action.payload.data.errors.length) {
            state.errorsIngredientsCleanUpList = action.payload.data.errors;
            return;
          }
          eventBus.emit(EventType.ADMIN_IMPORT_INGREDIENTS_CLEANUP_LIST_SUCCESS);
        },
      );
  },
});

export const {
  removeAdminImportMedications,
  removeAdminImportSynonymsList,
  removeAdminImportHighRiskSupplementsList,
  removeAdminImportSupplementsCleanUpList,
  removeAdminImportIngredientsCleanUpList,
} = adminImportMedicationsSlice.actions;

export default adminImportMedicationsSlice.reducer;
